import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../components/ui/select';
import { Input } from '../../../../../../../components/ui/input';
import { Button } from '../../../../../../../components/ui/button';
import { Badge } from '../../../../../../../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { renamePass } from '../../../../../../../redux/device/actions';
import { logWeldParameterPassesRenamed } from '../../../../../utils/productionAudit';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../../../../components/ui/table';
import Notification from '../../../../../../../components/ui/custom-notification';

interface RenamePassProps {
    jobData: any;
    projectId?: number;
    onRenameSuccess?: () => void;
}

interface PassData {
    PassName: string;
    PassPendName: string;
    PassEnable: string;
    PassGroupNumber: string;
    EndingAngle: string;
    Type: string;
    Torch?: string;
    __passIndex?: number;
    [key: string]: any;
}

interface RenameEntry {
    groupNo: string;
    torch: string;
    passName: string;
    newPassName: string;
    numberToAppend: string;
}

const RenamePass: React.FC<RenamePassProps> = ({ jobData, projectId, onRenameSuccess }) => {
    const dispatch = useDispatch();
    const [renameEntries, setRenameEntries] = useState<Map<string, RenameEntry>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState<string>('');
    const [missingFields, setMissingFields] = useState<Set<string>>(new Set());

    // Store timeout reference for cleanup
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Reset rename entries and loading when job data changes
    React.useEffect(() => {
        setRenameEntries(new Map());
        setIsLoading(false);
        // Clear any pending timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [jobData?.job_number]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Pass name options for dropdowns
    const passNameOptions = [
        { value: 'Hot', label: 'Hot' },
        { value: 'Root', label: 'Root' },
        { value: 'Fill', label: 'Fill' },
        { value: 'Last Fill', label: 'Last Fill' },
        { value: 'Strip', label: 'Strip' },
        { value: 'Cap', label: 'Cap' },
        { value: 'Disabled', label: 'Disabled' },
        { value: 'Weave', label: 'Weave' },
    ];

    // Generate numbers 1-20 for append dropdown
    const numberOptions = Array.from({ length: 20 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1)
    }));

    // Extract all PASS type entries from parsed_json_data
    const passEntries = useMemo(() => {
        if (!jobData?.parsed_json_data) return [];

        // Check if parsed_json_data is an array before filtering
        if (Array.isArray(jobData.parsed_json_data)) {
            return jobData.parsed_json_data
                .filter((item: any) => item.Type === 'PASS')
                .map((item: any, index: number) => ({
                    ...item,
                    __passIndex: index
                }));
        }

        return [];
    }, [jobData]);

    // Group passes by Group Number and determine torch type (LEAD or TRAIL)
    const groupedPasses = useMemo(() => {
        const grouped = new Map<string, { lead: PassData[]; trail: PassData[] }>();


        passEntries.forEach((pass: PassData, index: number) => {
            const groupKey = pass.PassGroupNumber || 'Unknown';

            if (!grouped.has(groupKey)) {
                grouped.set(groupKey, { lead: [], trail: [] });
            }

            const group = grouped.get(groupKey)!;

            // Use Torch field if available, otherwise try to infer
            const torch = (pass as any).Torch || pass.Torch || '';
            const torchLower = torch.toLowerCase();

          
            if (torchLower === 'lead') {
                group.lead.push(pass);
            } else if (torchLower === 'trail') {
                group.trail.push(pass);
            } else {
                // Fallback: try to infer from other fields
                const passName = pass.PassName || '';
                const passPendName = pass.PassPendName || '';

                if (passPendName.toLowerCase().includes('lead') || passName.toLowerCase().includes('lead')) {
                    group.lead.push(pass);
                } else if (passPendName.toLowerCase().includes('trail') || passName.toLowerCase().includes('trail')) {
                    group.trail.push(pass);
                } else {
                    // Default: alternate between lead and trail
                    if (group.lead.length <= group.trail.length) {
                        group.lead.push(pass);
                    } else {
                        group.trail.push(pass);
                    }
                }
            }
        });

    
        return Array.from(grouped.entries()).sort((a, b) => {
            const aNum = parseInt(a[0]) || 0;
            const bNum = parseInt(b[0]) || 0;
            return aNum - bNum;
        });
    }, [passEntries]);

    // Format pass name for display (extract code and parenthetical value)
    const formatPassName = (passName: string, passPendName?: string, passEnable?: string) => {
        // Check if pass is disabled via PassEnable field
        if (passEnable && passEnable.toLowerCase() === 'no') {
            if (passPendName && passPendName.trim()) {
                return { code: 'Disabled', value: passPendName.trim(), display: `Disabled (${passPendName.trim()})` };
            }
            return { code: 'Disabled', value: '', display: 'Disabled' };
        }

        // Handle ".." as Disabled
        if (passName.trim() === '..' || passName.trim() === '.') {
            if (passPendName && passPendName.trim()) {
                return { code: 'Disabled', value: passPendName.trim(), display: `Disabled (${passPendName.trim()})` };
            }
            return { code: 'Disabled', value: '', display: 'Disabled ()' };
        }

        // Extract pattern like "F1T (1T)" or "Disabled (OP)"
        const match = passName.match(/^([^(]+)\s*\(([^)]+)\)$/);
        if (match) {
            return { code: match[1].trim(), value: match[2].trim(), display: `${match[1].trim()} (${match[2].trim()})` };
        }

        // If pass name doesn't have parentheses but we have PassPendName, use it
        if (passPendName && passPendName.trim()) {
            return { code: passName.trim(), value: passPendName.trim(), display: `${passName.trim()} (${passPendName.trim()})` };
        }

        // If no parentheses and no pendant name
        return { code: passName.trim(), value: '', display: passName.trim() };
    };

    // Check if a group has any disabled passes
    const isGroupDisabled = (passes: { lead: PassData[]; trail: PassData[] }) => {
        const allPasses = [...(passes.lead || []), ...(passes.trail || [])];
        return allPasses.some(pass => pass.PassEnable && pass.PassEnable.toLowerCase() === 'no');
    };

    const handleNewPassNameChange = (groupKey: string, torch: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }
        if (missingFields.has(`${groupKey}-${torch}-name`)) {
            const newMissingFields = new Set(missingFields);
            newMissingFields.delete(`${groupKey}-${torch}-name`);
            setMissingFields(newMissingFields);
        }

        const entryKey = `${groupKey}-${torch}`;
        const current = renameEntries.get(entryKey) || {
            groupNo: groupKey,
            torch,
            passName: '',
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            newPassName: value
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(entryKey, updated);
        setRenameEntries(newEntries);
    };

    const handleNumberChange = (groupKey: string, torch: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }

        const entryKey = `${groupKey}-${torch}`;
        const current = renameEntries.get(entryKey) || {
            groupNo: groupKey,
            torch,
            passName: '',
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            numberToAppend: value
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(entryKey, updated);
        setRenameEntries(newEntries);
    };

    const handleApplyChanges = () => {
        
        if (!projectId || !jobData?.job_number) {
            console.error('Missing projectId or job_number');
            return;
        }

        if (isLoading) {
            console.log('P600z - Already loading, preventing duplicate call');
            return; // Prevent multiple clicks while loading
        }

        // Validate that all groups have pass names selected for both LEAD and TRAIL
        const missingGroups: string[] = [];
        groupedPasses.forEach(([groupKey, passes]) => {
            const leadEntry = renameEntries.get(`${groupKey}-lead`);
            const trailEntry = renameEntries.get(`${groupKey}-trail`);

            if (passes.lead && passes.lead.length > 0) {
                if (!leadEntry?.newPassName) {
                    missingGroups.push(`${groupKey} (LEAD)`);
                }
            }
            if (passes.trail && passes.trail.length > 0) {
                if (!trailEntry?.newPassName) {
                    missingGroups.push(`${groupKey} (TRAIL)`);
                }
            }
        });

        if (missingGroups.length > 0) {
            // Track which fields are missing for red border highlighting
            const missingFieldsSet = new Set<string>();
            groupedPasses.forEach(([groupKey, passes]) => {
                const leadEntry = renameEntries.get(`${groupKey}-lead`);
                const trailEntry = renameEntries.get(`${groupKey}-trail`);

                if (passes.lead && passes.lead.length > 0) {
                    if (!leadEntry?.newPassName) {
                        missingFieldsSet.add(`${groupKey}-lead-name`);
                    }
                }
                if (passes.trail && passes.trail.length > 0) {
                    if (!trailEntry?.newPassName) {
                        missingFieldsSet.add(`${groupKey}-trail-name`);
                    }
                }
            });
            setMissingFields(missingFieldsSet);
            Notification.warning('Please select pass name for all groups');
            return;
        }

        // Clear validation error and missing fields if validation passes
        setValidationError('');
        setMissingFields(new Set());

        // Build renamed_passes array from renameEntries
        const renamedPassesMap = new Map<string, any>(); // Use Map to prevent duplicates

        // Iterate through each rename entry (groupKey-torch format)
        renameEntries.forEach((entry, entryKey) => {
            if (!entry.newPassName) {
                return; // Skip if newPassName is missing
            }

            // Parse groupKey and torch from entryKey (e.g., "DA-lead" -> groupKey: "DA", torch: "lead")
            const parts = entryKey.split('-');
            const groupKey = parts[0] || entry.groupNo;
            const torch = parts[1] || entry.torch || 'lead';

            // Find the group data
            const groupData = groupedPasses.find(([key]) => key === groupKey);
            if (!groupData) return;

            const [, passes] = groupData;
            const torchPasses = torch.toLowerCase() === 'lead' ? passes.lead : passes.trail;

            // Create newPassName by combining selected name with number (e.g., "Root" + "1" = "Root 1")
            // Only append number if provided
            const newPassNameFull = entry.numberToAppend && entry.numberToAppend.trim()
                ? `${entry.newPassName} ${entry.numberToAppend}`
                : entry.newPassName + ' ';

            // Determine torch value (use actual torch ID from pass if available, otherwise use LEAD/TRAIL)
            const torchValue = torch.toLowerCase() === 'lead' ? 'LEAD' : 'TRAIL';

            // Add an entry for each pass in this group/torch combination
            torchPasses.forEach((pass) => {
                const formatted = formatPassName(pass.PassName || '', pass.PassPendName, pass.PassEnable);
                // Try to get actual torch ID from pass, fallback to LEAD/TRAIL
                const torchId = pass.Torch || torchValue;

                // If pass is disabled, use original name; otherwise use the new selected name
                const isDisabled = pass.PassEnable && pass.PassEnable.toLowerCase() === 'no';
                const finalNewPassName = isDisabled ? pass.PassName : newPassNameFull;

                // Create unique key to prevent duplicates: groupNumber-passNumber-torch
                const uniqueKey = `${groupKey}-${pass.Pass}-${torchId}`;


                // Only add if not already in map (prevents duplicates)
                if (!renamedPassesMap.has(uniqueKey)) {
                    renamedPassesMap.set(uniqueKey, {
                        groupNumber: groupKey,
                        passNumber: pass.Pass, // Store for sorting
                        passIndex: pass.__passIndex ?? 0,
                        torch: torchId,
                        passName: formatted.display,
                        newPassName: finalNewPassName
                    });
                }
            });
        });

        if (renamedPassesMap.size === 0) {
            console.log('No passes to rename');
            return;
        }

        // Keep rename payload in the same order as original PASS entries.
        // The rename API applies entries by pass order, so group sorting can mismatch names.
        const renamedPasses = Array.from(renamedPassesMap.values()).sort((a, b) => {
            return (a.passIndex ?? 0) - (b.passIndex ?? 0);
        }).map(({ passNumber, passIndex, ...rest }) => rest); // Remove sort-only fields from final output

        // Build the payload
        const payload = {
            project_id: projectId,
            job_number: jobData.job_number,
            is_renamed: true,
            json_payload: {
                renamed_passes: renamedPasses
            }
        };


        console.log('Applying rename changes with payload:', payload);

        logWeldParameterPassesRenamed({
            projectId,
            jobNumber: jobData.job_number,
            machineType: 'P600z',
            renamedPasses,
        });

       
        // Set loading state
        setIsLoading(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // // Dispatch Redux action to call API
         dispatch(renamePass(payload) as any);

        // Reset loading after a reasonable timeout (saga will handle success/error notifications)
        timeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            // Reset after applying only if loading completes
            setRenameEntries(new Map());
            // Call refresh callback to reload weld parameters
            if (onRenameSuccess) {
                onRenameSuccess();
            }
            timeoutRef.current = null;
        }, 3000); // 3 second timeout - adjust based on typical API response time
    };

    // Determine rowspan for group numbers
    const getRowspan = (groupKey: string) => {
        const group = groupedPasses.find(([key]) => key === groupKey);
        if (!group) return 1;
        const [, passes] = group;
        let count = 0;
        if (passes.lead && passes.lead.length > 0) count++;
        if (passes.trail && passes.trail.length > 0) count++;
        return count || 1;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Sticky action toolbar at the top — scoped to Rename Pass tab */}
            <div className="flex items-center justify-end gap-3 px-6 py-3 bg-white border-b border-[#E2E5EA] flex-shrink-0">
                <Button
                    variant="outline"
                    onClick={() => setRenameEntries(new Map())}
                    disabled={isLoading}
                    className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                >
                    Reset
                </Button>
                <Button
                    onClick={handleApplyChanges}
                    disabled={isLoading}
                    className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Applying...
                        </>
                    ) : (
                        'Apply Changes'
                    )}
                </Button>
            </div>

            {/* Table */}
            <Card className="flex-1 flex flex-col overflow-hidden min-h-0 border-0 shadow-none rounded-none">
                <CardContent className="p-0 flex-1 flex flex-col overflow-hidden min-h-0">
                    <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <div className="min-w-[900px]">
                            <Table className="w-full">
                                <TableHeader className="bg-gray-100">
                                    <TableRow className="bg-gray-100">
                                        <TableHead className="w-24 text-center sticky top-0 left-0 bg-gray-100 z-30 border-r-2 border-gray-300">Group No</TableHead>
                                        <TableHead className="w-32 text-center sticky top-0 bg-gray-100 z-20">Torch</TableHead>
                                        <TableHead className="min-w-40 sticky top-0 bg-gray-100 z-20">Pass Name</TableHead>
                                        <TableHead className="min-w-48 sticky top-0 bg-gray-100 z-20">New Pass Name</TableHead>
                                        <TableHead className="min-w-40 sticky top-0 bg-gray-100 z-20">Number to Append</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groupedPasses.map(([groupKey, passes]) => {
                                        const leadPasses = passes.lead || [];
                                        const trailPasses = passes.trail || [];
                                        const rowspan = getRowspan(groupKey);
                                        const groupDisabled = isGroupDisabled(passes);

                                        return (
                                            <React.Fragment key={groupKey}>
                                                {leadPasses.length > 0 && (
                                                    <TableRow>
                                                        <TableCell
                                                            rowSpan={rowspan}
                                                            className="border-r-2 border-gray-300 text-center align-middle bg-gray-50 sticky left-0 z-10"
                                                        >
                                                            <span className={`text-lg font-bold ${groupDisabled ? 'text-red-600' : ''}`}>
                                                                {groupKey}
                                                             
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-center font-semibold bg-blue-50 align-top py-4">
                                                            LEAD
                                                        </TableCell>
                                                        <TableCell className="text-red-600 align-top py-4">
                                                            <ul className="list-none space-y-1 pl-0">
                                                                {leadPasses.map((pass, idx) => {
                                                                    const formatted = formatPassName(pass.PassName || '', pass.PassPendName, pass.PassEnable);
                                                                    const isDisabled = pass.PassEnable && pass.PassEnable.toLowerCase() === 'no';
                                                                    return (
                                                                        <li key={idx} className="flex items-start">
                                                                            <span className="text-red-600 mr-2">•</span>
                                                                            <span className={`font-mono ${isDisabled ? 'text-red-600 font-bold' : ''}`}>
                                                                                {formatted.display}
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </TableCell>
                                                        <TableCell className="align-top py-4">
                                                            <div>
                                                                <Select
                                                                    value={renameEntries.get(`${groupKey}-lead`)?.newPassName || ''}
                                                                    onValueChange={(value) => handleNewPassNameChange(groupKey, 'lead', value)}
                                                                >
                                                                    <SelectTrigger className={missingFields.has(`${groupKey}-lead-name`) ? ' border-2' : ''}>
                                                                        <SelectValue placeholder="Select Pass Name" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {passNameOptions.map((option) => (
                                                                            <SelectItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                {missingFields.has(`${groupKey}-lead-name`) && (
                                                                    <p className="mt-1 text-xs text-red-500">Please select pass name</p>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="align-top py-4">
                                                            <div>
                                                                <Input
                                                                    type="number"
                                                                    value={renameEntries.get(`${groupKey}-lead`)?.numberToAppend || ''}
                                                                    onChange={(e) => handleNumberChange(groupKey, 'lead', e.target.value)}
                                                                    min={1}
                                                                    max={20}
                                                                    placeholder="Number to Append (Optional)"
                                                                />
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {trailPasses.length > 0 && (
                                                    <TableRow>
                                                        {leadPasses.length === 0 && (
                                                            <TableCell
                                                                rowSpan={rowspan}
                                                                className="border-r-2 border-gray-300 text-center align-middle bg-gray-50 sticky left-0 z-10"
                                                            >
                                                                <span className={`text-lg font-bold ${groupDisabled ? 'text-red-600' : ''}`}>
                                                                    {groupKey}
                                                                    {groupDisabled && <span className="ml-2 text-xs text-red-600">(Disabled)</span>}
                                                                </span>
                                                            </TableCell>
                                                        )}
                                                        <TableCell className="text-center font-semibold bg-orange-50 align-top py-4">
                                                            TRAIL
                                                        </TableCell>
                                                        <TableCell className="text-red-600 align-top py-4">
                                                            <ul className="list-none space-y-1 pl-0">
                                                                {trailPasses.map((pass, idx) => {
                                                                    const formatted = formatPassName(pass.PassName || '', pass.PassPendName, pass.PassEnable);
                                                                    const isDisabled = pass.PassEnable && pass.PassEnable.toLowerCase() === 'no';
                                                                    return (
                                                                        <li key={idx} className="flex items-start">
                                                                            <span className="text-red-600 mr-2">•</span>
                                                                            <span className={`font-mono ${isDisabled ? 'text-red-600 font-bold' : ''}`}>
                                                                                {formatted.display}
                                                                            </span>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </TableCell>
                                                        <TableCell className="align-top py-4">
                                                            <div>
                                                                <Select
                                                                    value={renameEntries.get(`${groupKey}-trail`)?.newPassName || ''}
                                                                    onValueChange={(value) => handleNewPassNameChange(groupKey, 'trail', value)}
                                                                >
                                                                    <SelectTrigger className={missingFields.has(`${groupKey}-trail-name`) ? ' border-2' : ''}>
                                                                        <SelectValue placeholder="Select Pass Name" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {passNameOptions.map((option) => (
                                                                            <SelectItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                {missingFields.has(`${groupKey}-trail-name`) && (
                                                                    <p className="mt-1 text-xs text-red-500">Please select pass name</p>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="align-top py-4">
                                                            <div>
                                                                <Input
                                                                    type="number"
                                                                    value={renameEntries.get(`${groupKey}-trail`)?.numberToAppend || ''}
                                                                    onChange={(e) => handleNumberChange(groupKey, 'trail', e.target.value)}
                                                                    min={1}
                                                                    max={20}
                                                                    placeholder="Number to Append (Optional)"
                                                                />
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                </CardContent>
            </Card>
        </div>
    );
};

export default RenamePass;
